/* eslint-disable react/prop-types */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Container, Row, Col, Form, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'bootstrap/dist/css/bootstrap.min.css';
import StatisticsCards from '../component/StatisticsCards';
import { sessionObjects } from '../session/sessionData';

const domain = window.location.hostname;
let xHost = '';
if (domain.includes('.uat')) {
  xHost = 'fcs-api.uat.farmsio.com';
} else {
  xHost = 'fcs-beam-api-v2.farmsio.com';
}

const FilterableDashboard = ({
  keyId,
  title,
  stateId,
  onDistrictChange,
  seasonData
}) => {
  // State for filters
  const [filters, setFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [districtFilterName, setDistrictFilterName] = useState(null);

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardTitle, setDashboardTitle] = useState(title || 'Dashboard');

  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);

  // Refs to prevent infinite loops
  const isInitialMount = useRef(true);
  const prevFilterValues = useRef({});
  const initialLoadComplete = useRef(false);
  const seasonFilterApplied = useRef(false);

  // Headers for API calls
  const headers = {
    'x-client-host': xHost,
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionObjects.access_token}`
  };

  // API base URL
  const apiBaseUrl = `${process.env.REACT_APP_FCS_API_URL}`;

  // Find matching season based on selected marketing year and crop type
  const findMatchingSeason = (marketingYear, cropType) => {
    if (!marketingYear || !cropType || !seasonData || seasonData.length === 0) {
      return null;
    }

    // Get the marketing year and crop type filters
    const marketingYearFilter = filters.find(
      filter => filter.modelName === 'marketing_year'
    );

    const cropTypeFilter = filters.find(
      filter => filter.modelName === 'crop_type_fcs'
    );

    if (!marketingYearFilter || !cropTypeFilter) {
      return null;
    }

    // Get dropdown options for these filters
    const marketingYearOptions =
      dropdownOptions[marketingYearFilter.dataModel] || [];
    const cropTypeOptions = dropdownOptions[cropTypeFilter.dataModel] || [];

    // Special handling for initial load where cropType might be an ID (like "2")
    let isInitialLoad = false;
    if (
      !isNaN(parseInt(cropType)) &&
      cropTypeFilter.value &&
      cropTypeFilter.value.value === cropType
    ) {
      isInitialLoad = true;
      // console.log('Initial load detected - cropType is numeric ID:', cropType);
    }

    // First, try to find the crop type by ID in dropdown options
    let marketingYearOption, cropTypeOption;

    // For marketing year, find by ID or value
    marketingYearOption = marketingYearOptions.find(
      option => (option.id || option.value) === marketingYear
    );

    // For crop type, check if it's initial load with ID value
    if (isInitialLoad) {
      // On initial load, we might have the crop type ID but need to find the actual text value
      // First, look in the filter's default value.value
      if (cropTypeFilter.value && cropTypeFilter.value.value) {
        // Try to find a matching season directly using this ID
        const matchingSeasonByCropId = seasonData.find(
          season => season.id === cropType
        );

        if (matchingSeasonByCropId) {
          // Use this season's info directly
          cropTypeOption = { value: matchingSeasonByCropId.Season };
        } else {
          // Look through options to find one with matching ID
          cropTypeOption = cropTypeOptions.find(
            option => (option.id || option.value) === cropType
          );
        }
      }
    } else {
      // Normal case: find crop type option by ID
      cropTypeOption = cropTypeOptions.find(
        option => (option.id || option.value) === cropType
      );
    }

    if (!marketingYearOption || !cropTypeOption) {
      // Special handling for initial load: try finding the crop type directly from season data
      if (isInitialLoad) {
        const matchingSeasonByCropId = seasonData.find(
          season => season.id === cropType
        );

        if (matchingSeasonByCropId) {
          cropTypeOption = { value: matchingSeasonByCropId.Season };
        }
      }

      // If we still don't have options, try to salvage the situation
      if (!marketingYearOption) {
        marketingYearOption = { value: marketingYear };
      }

      if (!cropTypeOption) {
        cropTypeOption = { value: cropType };
      }
    }

    // Get text values from options
    const marketingYearText =
      marketingYearOption.text || marketingYearOption.value;
    const cropTypeText = cropTypeOption.text || cropTypeOption.value;

    // In case of initial load with ID values, try to find the corresponding season directly
    if (isInitialLoad) {
      // Special handling for numeric crop type IDs on initial load
      const cropTypeId = cropType;

      // Try to find a direct match by ID first
      const seasonByIdMatch = seasonData.find(s => s.id === cropTypeId);
      if (seasonByIdMatch) {
        // Use this season directly and skip the text-based matching
        setSelectedSeason(seasonByIdMatch);

        // Use the dates from this season
        try {
          const fromDateStr = seasonByIdMatch['From Date'];
          const toDateStr = seasonByIdMatch['To Date'];

          // Parse the dates
          const fromDate = new Date(fromDateStr);
          const toDate = new Date(toDateStr);

          if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
            // Set min and max dates for date pickers
            setMinDate(fromDate);
            setMaxDate(toDate);

            // Find from_date and to_date filters
            const fromDateFilter = filters.find(
              filter =>
                filter.placeHolder?.toLowerCase() === 'from date' ||
                filter.placeHolder?.toLowerCase().includes('from date') ||
                filter.modelName?.toLowerCase().includes('from_date')
            );

            const toDateFilter = filters.find(
              filter =>
                filter.placeHolder?.toLowerCase() === 'to date' ||
                filter.placeHolder?.toLowerCase().includes('to date') ||
                filter.modelName?.toLowerCase().includes('to_date')
            );

            if (fromDateFilter && toDateFilter) {
              // Format dates for the filter (DD/MM/YYYY)
              const formattedFromDate = formatDate(fromDate);
              const formattedToDate = formatDate(toDate);

              // Update filter values with the new dates
              const updatedFilterValues = { ...filterValues };
              updatedFilterValues[fromDateFilter.modelName] = formattedFromDate;
              updatedFilterValues[toDateFilter.modelName] = formattedToDate;

              // Set the filter values
              setFilterValues(updatedFilterValues);
              seasonFilterApplied.current = true;

              // Fetch dashboard data with the updated filter values
              setTimeout(() => {
                fetchDashboardData(true, updatedFilterValues);
              }, 50);

              return seasonByIdMatch;
            }
          }
        } catch (err) {
          console.error('Error processing season dates:', err);
        }
      }
    }

    // Try to find matching season - checking both exact and case-insensitive matches
    let matchingSeason = seasonData.find(
      season =>
        season['Season'] === cropTypeText &&
        season['Season Year'] === marketingYearText
    );

    if (!matchingSeason) {
      // Try case-insensitive match
      matchingSeason = seasonData.find(
        season =>
          season['Season'].toLowerCase() === cropTypeText.toLowerCase() &&
          season['Season Year'] === marketingYearText
      );
    }

    // If still no match, try with Season Calendar field
    if (!matchingSeason) {
      // Look for Season Calendar that contains both values
      matchingSeason = seasonData.find(
        season =>
          season['Season Calendar'] &&
          season['Season Calendar']
            .toLowerCase()
            .includes(cropTypeText.toLowerCase()) &&
          season['Season Calendar'].includes(marketingYearText.split('-')[0]) // Match just the year part
      );
    }

    if (matchingSeason) {
      setSelectedSeason(matchingSeason);

      try {
        // Parse From Date and To Date from season data
        const fromDateStr = matchingSeason['From Date'];
        const toDateStr = matchingSeason['To Date'];

        // Parse the dates
        const fromDate = new Date(fromDateStr);
        const toDate = new Date(toDateStr);

        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
          // Set min and max dates for date pickers
          setMinDate(fromDate);
          setMaxDate(toDate);

          // Find from_date and to_date filters
          const fromDateFilter = filters.find(
            filter =>
              filter.placeHolder?.toLowerCase() === 'from date' ||
              filter.placeHolder?.toLowerCase().includes('from date') ||
              filter.modelName?.toLowerCase().includes('from_date')
          );

          const toDateFilter = filters.find(
            filter =>
              filter.placeHolder?.toLowerCase() === 'to date' ||
              filter.placeHolder?.toLowerCase().includes('to date') ||
              filter.modelName?.toLowerCase().includes('to_date')
          );

          if (fromDateFilter && toDateFilter) {
            // Format dates for the filter (DD/MM/YYYY)
            const formattedFromDate = formatDate(fromDate);
            const formattedToDate = formatDate(toDate);

            // Update filter values with the new dates
            const updatedFilterValues = { ...filterValues };
            updatedFilterValues[fromDateFilter.modelName] = formattedFromDate;
            updatedFilterValues[toDateFilter.modelName] = formattedToDate;

            // Set the filter values
            setFilterValues(updatedFilterValues);
            seasonFilterApplied.current = true;

            // Fetch dashboard data with the updated filter values
            setTimeout(() => {
              fetchDashboardData(true, updatedFilterValues);
            }, 50);

            return matchingSeason;
          }
        }
      } catch (err) {
        console.error('Error processing season dates:', err);
      }
    } else {
      console.log(
        'No matching season found for',
        cropTypeText,
        marketingYearText
      );
    }

    return null;
  };

  // Fetch dashboard data and extract filter fields
  const fetchDashboardData = async (
    applyParams = false,
    explicitFilterValues = null
  ) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${apiBaseUrl}api/documents/get-list-record-sets`;

      // Use explicit values if provided, otherwise use state
      const filterValuesToUse = explicitFilterValues || filterValues;

      // Build params from filter values
      let params = {
        id: '',
        param1: '',
        param2: '',
        param3: '',
        param4: '',
        param5: '',
        param6: '',
        param7: '',
        param8: '',
        param9: '',
        param10: ''
      };

      // Important change: If stateId is provided, use it as the district filter value
      // First, determine if there's a district filter
      let districtFilterFound = false;

      if (applyParams && filters.length > 0) {
        // Check filters for a district filter
        const districtFilter = filters.find(
          filter =>
            filter.placeHolder?.toLowerCase().includes('district') ||
            filter.modelName?.toLowerCase().includes('district')
        );

        if (districtFilter && stateId) {
          // Set the district filter value using stateId
          filterValuesToUse[districtFilter.modelName] = stateId;
          districtFilterFound = true;
          if (!districtFilterName) {
            setDistrictFilterName(districtFilter.modelName);
          }
        }
        params.id = filterValuesToUse[filters[0].modelName] || '';

        filters.slice(1).forEach((filter, index) => {
          if (index < 10) {
            params[`param${index + 1}`] =
              filterValuesToUse[filter.modelName] || '';
          }
        });
      }

      // If no district filter was found but stateId is provided, use it as param1
      if (stateId && !districtFilterFound && !params.param1) {
        params.param1 = stateId;
      }

      const requestBody = {
        userId: 'bd30c685-6446-4c9b-9336-a96c66550072',
        key: keyId,
        ...params,
        limit: '100',
        offset: '0',
        sessionObjects: sessionObjects
      };

      const response = await axios.post(url, requestBody, { headers });

      if (response.data && response.data.Status === 'SUCCESS') {
        // Extract the dashboard data
        const recordsView = response.data.Result.recordsView?.[0];

        if (recordsView) {
          // Handle first-time load
          if (recordsView.filterFields && (!filters.length || !applyParams)) {
            setFilters(recordsView.filterFields);
            // Identify the district filter during initial load
            const districtFilter = recordsView.filterFields.find(
              filter =>
                filter.placeHolder?.toLowerCase().includes('district') ||
                filter.modelName?.toLowerCase().includes('district')
            );

            if (districtFilter) {
              setDistrictFilterName(districtFilter.modelName);
            }

            // Initialize filter values from default values
            const initialValues = {};
            const dropdownsToFetch = [];

            recordsView.filterFields.forEach(filter => {
              // Store initial values from the API
              initialValues[filter.modelName] = filter.value?.value || '';

              // Track dropdown models that need to be fetched
              if (filter.inputType === 'dropdown' && filter.dataModel) {
                dropdownsToFetch.push({
                  dataModel: filter.dataModel,
                  modelName: filter.modelName
                });
              }
            });

            // Only set if we're not applying filters (initial load)
            if (!applyParams) {
              setFilterValues(initialValues);

              // Fetch all dropdown options for initial load
              const fetchDropdownsForInitialLoad = async () => {
                try {
                  // Fetch all dropdown options in parallel
                  const results = await Promise.all(
                    dropdownsToFetch.map(({ dataModel }) =>
                      fetchDropdownOptions(dataModel, true)
                    )
                  );

                  // Set initialLoadComplete flag true before matching seasons
                  initialLoadComplete.current = true;

                  // Setup crop type and marketing year for season matching
                  const marketingYear = initialValues['marketing_year'];
                  const cropType = initialValues['crop_type_fcs'];

                  if (seasonData && seasonData.length > 0) {
                    // Wait a bit to ensure all state is updated
                    setTimeout(() => {
                      // Find the matching season directly from seasonData using ID
                      const matchingSeasonById =
                        cropType && seasonData.find(s => s.id === cropType);

                      if (matchingSeasonById) {
                        // Process dates from the found season
                        const fromDateStr = matchingSeasonById['From Date'];
                        const toDateStr = matchingSeasonById['To Date'];

                        if (fromDateStr && toDateStr) {
                          const fromDate = new Date(fromDateStr);
                          const toDate = new Date(toDateStr);

                          if (
                            !isNaN(fromDate.getTime()) &&
                            !isNaN(toDate.getTime())
                          ) {
                            // Set min and max dates for date pickers
                            setMinDate(fromDate);
                            setMaxDate(toDate);
                            setSelectedSeason(matchingSeasonById);

                            // Find from_date and to_date filters
                            const fromDateFilter =
                              recordsView.filterFields.find(
                                filter =>
                                  filter.placeHolder?.toLowerCase() ===
                                    'from date' ||
                                  filter.placeHolder
                                    ?.toLowerCase()
                                    .includes('from date') ||
                                  filter.modelName
                                    ?.toLowerCase()
                                    .includes('from_date')
                              );

                            const toDateFilter = recordsView.filterFields.find(
                              filter =>
                                filter.placeHolder?.toLowerCase() ===
                                  'to date' ||
                                filter.placeHolder
                                  ?.toLowerCase()
                                  .includes('to date') ||
                                filter.modelName
                                  ?.toLowerCase()
                                  .includes('to_date')
                            );

                            if (fromDateFilter && toDateFilter) {
                              // Format dates for the filter (DD/MM/YYYY)
                              const formattedFromDate = formatDate(fromDate);
                              const formattedToDate = formatDate(toDate);

                              // Update filter values with the new dates
                              const updatedFilterValues = { ...initialValues };
                              updatedFilterValues[fromDateFilter.modelName] =
                                formattedFromDate;
                              updatedFilterValues[toDateFilter.modelName] =
                                formattedToDate;

                              // Set the filter values and refresh data
                              setFilterValues(updatedFilterValues);
                              seasonFilterApplied.current = true;

                              // Fetch dashboard data with the updated filter values
                              setTimeout(() => {
                                fetchDashboardData(true, updatedFilterValues);
                              }, 50);
                            }
                          }
                        }
                      } else {
                        // If no direct ID match, try the standard matching
                        if (marketingYear && cropType) {
                          findMatchingSeason(marketingYear, cropType);
                        }
                      }
                    }, 100);
                  }
                } catch (error) {
                  console.error('Error fetching dropdown options:', error);
                }
              };

              fetchDropdownsForInitialLoad();
            }
          }

          // Set dashboard data
          const recordsData = recordsView.datas || [];
          setDashboardData(recordsData);

          // Set dashboard title if available
          if (recordsView.title?.value && !title) {
            setDashboardTitle(recordsView.title.value);
          }
        } else {
          setError('No data available in the response');
        }
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch dropdown options
  const fetchDropdownOptions = async (dataModel, isInitialLoad = false) => {
    try {
      const url = `${apiBaseUrl}api/sqladmin/get-dropdown-data-unauthenticated`;
      const requestBody = {
        userId: 'bd30c685-6446-4c9b-9336-a96c66550072',
        dataModel: dataModel,
        id: '',
        searchString: '',
        param1: '',
        param2: '',
        param3: '',
        param4: '',
        param5: '',
        param6: '',
        param7: '',
        param8: '',
        param9: '',
        param10: '',
        sessionObjects: sessionObjects
      };

      const response = await axios.post(url, requestBody, { headers });

      if (response.data && response.data.Status === 'SUCCESS') {
        const options = response.data.Result || [];
        // Special handling for crop_type_fcs to log ID to value mappings
        if (dataModel === 'crop_type_fcs') {
          options.forEach(opt => {
            // console.log(`ID: ${opt.id}, Value: ${opt.value}`);
          });

          // If we have both season data and crop type options, try to build a mapping
          if (seasonData && seasonData.length) {
            // console.log('Building crop type ID to Season value mapping:');
            const cropTypeIdToValueMap = {};

            // From dropdown options
            options.forEach(opt => {
              if (opt.id && opt.value) {
                cropTypeIdToValueMap[opt.id] = opt.value;
              }
            });

            // From season data
            seasonData.forEach(season => {
              if (season.id && season.Season) {
                cropTypeIdToValueMap[season.id] = season.Season;
              }
            });

            // console.log('Crop type ID to value mapping:', cropTypeIdToValueMap);
          }
        }

        setDropdownOptions(prev => ({
          ...prev,
          [dataModel]: options
        }));

        return options;
      }
    } catch (error) {
      console.error(`Error fetching dropdown options for ${dataModel}:`, error);
    }

    return [];
  };

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      try {
        // If we have season data, log it
        if (seasonData && seasonData.length) {
          // console.log('Season data available on initial load:', seasonData);

          // Look for any crop type ID in the season data to map IDs to values
          seasonData.forEach(season => {
            // console.log(`Season ID: ${season.id}, Type: ${season.Season}`);
          });
        }

        // Fetch dashboard data
        await fetchDashboardData();
      } catch (err) {
        console.error('Error during initial data fetch:', err);
        setError(
          'Failed to load initial data. Please try refreshing the page.'
        );
        setLoading(false);
      }
    };

    loadData();
  }, [keyId]);

  // Update filter values and refetch data when stateId changes
  useEffect(() => {
    if (stateId && filters.length > 0) {
      // Find district filter if it exists
      const districtFilter = filters.find(
        filter =>
          filter.placeHolder?.toLowerCase().includes('district') ||
          filter.modelName?.toLowerCase().includes('district')
      );

      if (districtFilter) {
        // Safely update the filter values with the new district ID
        const newFilterValues = {
          ...filterValues,
          [districtFilter.modelName]: stateId
        };

        // Update filter values state to reflect the new selection
        setFilterValues(newFilterValues);

        // Fetch dashboard data with updated filter values
        fetchDashboardData(true, newFilterValues);
      } else {
        // If no district filter found, just fetch with stateId as a parameter
        fetchDashboardData(true);
      }
    }
  }, [stateId]);

  // Check for season match when both marketing year and crop type are selected
  useEffect(() => {
    // Skip on initial render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Check if marketing year or crop type changed
    const hasMarketingYearChanged =
      filterValues['marketing_year'] !==
      prevFilterValues.current['marketing_year'];

    const hasCropTypeChanged =
      filterValues['crop_type_fcs'] !==
      prevFilterValues.current['crop_type_fcs'];

    // Update previous filter values
    prevFilterValues.current = { ...filterValues };

    // If neither marketing year nor crop type changed, skip this effect
    if (!hasMarketingYearChanged && !hasCropTypeChanged) {
      return;
    }

    const marketingYear = filterValues['marketing_year'];
    const cropType = filterValues['crop_type_fcs'];

    if (marketingYear && cropType) {
      // Reset the season filter applied flag
      seasonFilterApplied.current = false;

      // Find matching season and update dates
      findMatchingSeason(marketingYear, cropType);
    }
  }, [filterValues['marketing_year'], filterValues['crop_type_fcs']]);

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    console.log(`Filter changed: ${filterName} = ${value}`);

    // Create new filter values
    const newFilterValues = {
      ...filterValues,
      [filterName]: value
    };

    // Update filter values
    setFilterValues(newFilterValues);

    // Check if this is the district filter and notify parent if it changes
    if (filterName === districtFilterName && onDistrictChange) {
      onDistrictChange(value);
    }

    // For marketing_year and crop_type_fcs, the useEffect will handle finding the matching season
    // For other filters, fetch data directly
    if (filterName !== 'marketing_year' && filterName !== 'crop_type_fcs') {
      fetchDashboardData(true, newFilterValues);
    }
  };

  // Function to parse date string (DD/MM/YYYY) to Date object
  const parseDate = dateString => {
    if (!dateString) return null;

    try {
      // Handle different date formats
      if (typeof dateString === 'string') {
        // Check if it's already in DD/MM/YYYY format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
          const [day, month, year] = dateString.split('/').map(Number);
          // Validate the date components before creating the Date object
          if (
            isNaN(day) ||
            isNaN(month) ||
            isNaN(year) ||
            day < 1 ||
            day > 31 ||
            month < 1 ||
            month > 12 ||
            year < 1900
          ) {
            return null;
          }
          return new Date(year, month - 1, day);
        }
        // Handle API date format (e.g. "Mon Aug 08 2022 00:00:00 GMT+0000")
        else if (dateString.includes('GMT')) {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            return date;
          }
          return null;
        }
        // Try default Date parsing as a fallback
        else {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };

  // Function to format Date to string (DD/MM/YYYY)
  const formatDate = dateInput => {
    // console.log('Formatting date input:', dateInput);

    // If the input is already a string in DD/MM/YYYY format, return it
    if (
      typeof dateInput === 'string' &&
      /^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)
    ) {
      // console.log('Already in DD/MM/YYYY format:', dateInput);
      return dateInput;
    }

    // Handle string dates in various formats
    if (typeof dateInput === 'string') {
      try {
        // Create a new Date object from the string
        const parsedDate = new Date(dateInput);
        if (!isNaN(parsedDate.getTime())) {
          const day = String(parsedDate.getDate()).padStart(2, '0');
          const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
          const year = parsedDate.getFullYear();
          const formatted = `${day}/${month}/${year}`;
          // console.log('Formatted string date to:', formatted);
          return formatted;
        }
      } catch (error) {
        console.error('Error formatting string date:', error);
        return '';
      }
    }

    // Handle Date objects
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      try {
        const day = String(dateInput.getDate()).padStart(2, '0');
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const year = dateInput.getFullYear();
        const formatted = `${day}/${month}/${year}`;
        // console.log('Formatted Date object to:', formatted);
        return formatted;
      } catch (error) {
        console.error('Error formatting Date object:', error);
        return '';
      }
    }

    console.warn('Could not format date input:', dateInput);
    return '';
  };

  // Render filter form - fixed to properly handle different input types
  const renderFilterForm = () => {
    if (!filters.length) return null;

    const customStyles = `
  .custom-datepicker-container {
    width: 100%;
    display: block;
    position: relative;
  }
  .custom-datepicker-wrapper {
    width: 100% !important;
    display: block !important;
  }
  .custom-datepicker {
    width: 100% !important;
    display: block !important;
    box-sizing: border-box !important;
  }
  .react-datepicker-popper {
    z-index: 9999;
  }
`;

    // For debugging, log the current filter values for date fields
    const dateFilters = filters.filter(f => f.inputType === 'date');
    if (dateFilters.length > 0) {
      // console.log('Current date filter values:');
      // dateFilters.forEach(filter => {
      //   console.log(
      //     `${filter.modelName}: ${filterValues[filter.modelName] || 'not set'}`
      //   );
      //   console.log('Parsed date:', parseDate(filterValues[filter.modelName]));
      // });
    }

    return (
      <div className="mb-3 p-3 border rounded bg-light">
        <style>{customStyles}</style>
        <Row className="g-2">
          {filters.map((filter, index) => (
            <Col md={3} sm={6} xs={12} className="mb-2" key={index}>
              <Form.Group>
                <Form.Label className="small mb-1 d-block">
                  {filter.placeHolder}
                </Form.Label>

                {filter.inputType === 'dropdown' && (
                  <Form.Select
                    size="sm"
                    required={filter.isRequired}
                    value={filterValues[filter.modelName] || ''}
                    className="py-1"
                    onChange={e =>
                      handleFilterChange(filter.modelName, e.target.value)
                    }
                  >
                    <option value="">Select {filter.placeHolder}</option>
                    {dropdownOptions[filter.dataModel]?.map((option, idx) => (
                      <option key={idx} value={option.id || option.value}>
                        {option.value || option.text}
                      </option>
                    ))}
                  </Form.Select>
                )}

                {filter.inputType === 'date' && (
                  <div className="custom-datepicker-container">
                    <DatePicker
                      selected={parseDate(filterValues[filter.modelName])}
                      onChange={date => {
                        if (date && !isNaN(date.getTime())) {
                          handleFilterChange(
                            filter.modelName,
                            formatDate(date)
                          );
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      className="form-control form-control-sm py-1 custom-datepicker"
                      wrapperClassName="custom-datepicker-wrapper"
                      required={filter.isRequired}
                      placeholderText={`Select ${filter.placeHolder}`}
                      minDate={
                        filter.placeHolder?.toLowerCase().includes('from date')
                          ? minDate
                          : undefined
                      }
                      maxDate={
                        filter.placeHolder?.toLowerCase().includes('to date')
                          ? maxDate
                          : undefined
                      }
                      // Key with both filter values and min/max dates to force re-render
                      key={`${filter.modelName}-${
                        filterValues[filter.modelName] || 'empty'
                      }-${minDate?.getTime() || 'undefined'}-${
                        maxDate?.getTime() || 'undefined'
                      }`}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
          ))}
        </Row>

        {/* {selectedSeason && (
          <div className="mt-2 small text-info">
            <strong>Selected Season:</strong>{' '}
            {selectedSeason['Season Calendar']}
            {minDate &&
              maxDate &&
              ` (${formatDate(minDate)} - ${formatDate(maxDate)})`}
          </div>
        )} */}
      </div>
    );
  };

  // Loading spinner
  if (loading && dashboardData.length === 0) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading dashboard data...</p>
      </div>
    );
  }

  // Error state
  if (error && dashboardData.length === 0) {
    return (
      <div className="alert alert-danger my-3" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* Filter Form */}
      {renderFilterForm()}

      {/* Results */}
      {loading && (
        <div className="text-center my-3">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Refreshing data...</span>
        </div>
      )}

      <StatisticsCards statsData={dashboardData} lg={3} />

      {dashboardData.length === 0 && !loading && (
        <div className="alert alert-info" role="alert">
          No data available.
        </div>
      )}
    </div>
  );
};

export default FilterableDashboard;
